import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
// Removed problematic usePSEOSEO hook to fix React hooks error
import { RelatedPSEOPages, PSEOBreadcrumbs } from '../components/InternalLinks';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { 
  BookOpen, 
  Palette, 
  Target, 
  ArrowRight,
  CheckCircle,
  Star,
  Users,
  Zap,
  TrendingUp,
  Award,
  Clock,
  Download
} from 'lucide-react';
import { PSEO_GENRES, PSEO_TOOLS, getRelatedPages, generatePSEOPages } from '../data/pseoData';

const PSEOPage = () => {
  const { genre: genreParam, tool: toolParam } = useParams();
  const navigate = useNavigate();
  const [genre, setGenre] = useState(null);
  const [tool, setTool] = useState(null);
  const [allPages, setAllPages] = useState([]);
  
  useEffect(() => {
    // Parse URL parameters to determine genre and tool
    const urlPath = window.location.pathname.toLowerCase();
    let foundGenre = null;
    let foundTool = null;
    
    console.log('PSEO Debug - URL Path:', urlPath);
    
    // Generate all possible pages for reference
    const pages = generatePSEOPages();
    setAllPages(pages);
    
    // Find matching genre
    Object.keys(PSEO_GENRES).forEach(g => {
      if (urlPath.includes(g)) {
        foundGenre = g;
        console.log('PSEO Debug - Found genre:', g);
      }
    });
    
    // Find matching tool
    Object.keys(PSEO_TOOLS).forEach(t => {
      if (urlPath.includes(t)) {
        foundTool = t;
        console.log('PSEO Debug - Found tool:', t);
      }
    });
    
    // Special cases for different URL patterns
    if (urlPath.includes('how-to-write')) {
      foundTool = 'writing-guide';
      console.log('PSEO Debug - Special case: writing-guide');
    }
    if (urlPath.includes('cover-generator')) {
      foundTool = 'cover-generator';
      console.log('PSEO Debug - Special case: cover-generator');
    }
    if (urlPath.includes('creator') || (urlPath.includes('generator') && !urlPath.includes('cover-generator'))) {
      foundTool = 'book-creator';
      console.log('PSEO Debug - Special case: book-creator');
    }
    if (urlPath.includes('guide') && !urlPath.includes('writing')) {
      foundTool = 'publishing-guide';
      console.log('PSEO Debug - Special case: publishing-guide');
    }
    
    console.log('PSEO Debug - Final genre:', foundGenre, 'tool:', foundTool);
    
    if (foundGenre && foundTool) {
      setGenre(foundGenre);
      setTool(foundTool);
      
      // Scroll to top when page loads or route changes
      if (typeof window !== 'undefined') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } else {
      // If no valid combination found, set fallback values instead of navigating
      console.log('PSEO Debug - No valid combination found, using fallback');
      setGenre('romance');
      setTool('book-creator');
    }
  }, [genreParam, toolParam, navigate]);

  // Calculate all data and execute ALL hooks BEFORE any conditional returns
  const genreData = genre ? PSEO_GENRES[genre] : PSEO_GENRES['romance']; // Fallback
  const toolData = tool ? PSEO_TOOLS[tool] : PSEO_TOOLS['book-creator']; // Fallback
  const relatedPages = getRelatedPages(genre || 'romance', tool || 'book-creator', allPages);
  
  const pageTitle = genreData && toolData ? `${genreData.title} ${toolData.title}` : 'AI BookMoney';
  const pageDescription = genreData && toolData ? `${toolData.description} for ${genre || 'romance'} books. ${genreData.description} Join 5,000+ authors using AI BookMoney.` : 'AI-powered book creation platform';
  const keywords = genreData && toolData ? [...genreData.keywords, ...toolData.title.toLowerCase().split(' ')].join(', ') : 'AI book generator, KDP publishing';
  
  // Use the actual current URL path as canonical to avoid mismatches
  const canonicalUrl = typeof window !== 'undefined' ? window.location.pathname : '/';

  // Enhanced SEO data for article schema  
  const articleData = {
    author: "AI BookMoney Team",
    publishedTime: "2024-12-25T00:00:00Z",
    section: "Publishing",
    tags: genreData && toolData ? [genreData.title, toolData.title.replace('-', ' '), "book publishing", "KDP", "self publishing"] : ["book publishing"]
  };

  // Schema markup for SEO
  const schemaMarkup = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": pageTitle,
    "description": pageDescription,
    "author": {
      "@type": "Organization", 
      "name": "AI BookMoney"
    },
    "publisher": {
      "@type": "Organization",
      "name": "AI BookMoney",
      "logo": {
        "@type": "ImageObject",
        "url": "https://aibookmoney.com/logo.svg"
      }
    },
    "datePublished": "2024-12-25T00:00:00Z",
    "dateModified": "2024-12-25T00:00:00Z",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://aibookmoney.com${canonicalUrl}`
    },
    "about": {
      "@type": "Thing",
      "name": genreData ? `${genreData.title} Book Writing` : "Book Writing",
      "description": genreData ? genreData.description : "AI-powered book creation"
    }
  };

  // Removed inline meta injection - using SEOHead component only for single source of truth

  // NOW do conditional rendering AFTER all hooks have executed
  if (!genre || !tool || !genreData || !toolData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 to-indigo-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-purple-400"></div>
      </div>
    );
  }

  return (
    <>
      <SEOHead 
        title={pageTitle}
        description={pageDescription}
        keywords={keywords}
        canonical={canonicalUrl}
        ogTitle={`${pageTitle} - AI BookMoney`}
        ogDescription={pageDescription}
        ogImage="/ai-book-generation.png"
        ogUrl={canonicalUrl}
        articleData={articleData}
        schemaData={schemaMarkup}
      />

      <div className="min-h-screen bg-gradient-to-br from-purple-900 to-indigo-900">
        <Navbar />
        <PSEOBreadcrumbs genre={genre} tool={tool} genreData={genreData} toolData={toolData} />

        {/* Hero Section */}
        <div className="pt-20 pb-12 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
                {genreData.title} {toolData.title}
              </h1>
              <p className="text-xl md:text-2xl text-gray-300 mb-8 max-w-4xl mx-auto">
                {toolData.description} for {genre} books. {genreData.description}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
                <Link
                  to="/generate"
                  className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold py-4 px-8 rounded-xl transition-all duration-300 transform hover:scale-105 flex items-center"
                >
                  <Zap className="h-5 w-5 mr-2" />
                  Start Creating Now
                  <ArrowRight className="h-5 w-5 ml-2" />
                </Link>
                
                <div className="flex items-center text-gray-300">
                  <Users className="h-5 w-5 mr-2" />
                  <span>Join 5,000+ successful authors</span>
                </div>
              </div>

              {/* Social Proof */}
              <div className="flex justify-center items-center space-x-6 text-yellow-400">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-5 w-5 fill-current" />
                ))}
                <span className="text-white ml-2">4.9/5 from 2,847 authors</span>
              </div>
            </div>
          </div>
        </div>

        {/* Benefits Section */}
        <div className="py-16 px-4 bg-gray-900/50">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12">
              Why Choose Our {genreData.title} {toolData.title}?
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {toolData.benefits.map((benefit, index) => (
                <div key={index} className="bg-gray-800 rounded-xl p-6 text-center">
                  <div className="bg-purple-600 rounded-full p-3 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                    <CheckCircle className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">{benefit.split(' ')[0]} {benefit.split(' ')[1]}</h3>
                  <p className="text-gray-400">{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Genre-Specific Tips */}
        <div className="py-16 px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12">
              Expert {genreData.title} Writing Tips
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              <div className="bg-gray-800 rounded-xl p-8">
                <h3 className="text-2xl font-bold text-white mb-6 flex items-center">
                  <BookOpen className="h-6 w-6 mr-3 text-purple-400" />
                  Writing Strategies
                </h3>
                <ul className="space-y-4">
                  {genreData.tips.map((tip, index) => (
                    <li key={index} className="flex items-start text-gray-300">
                      <CheckCircle className="h-5 w-5 text-green-400 mr-3 mt-0.5 flex-shrink-0" />
                      {tip}
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className="bg-gray-800 rounded-xl p-8">
                <h3 className="text-2xl font-bold text-white mb-6 flex items-center">
                  <Palette className="h-6 w-6 mr-3 text-pink-400" />
                  Cover Design Tips
                </h3>
                <ul className="space-y-4">
                  {genreData.coverTips.map((tip, index) => (
                    <li key={index} className="flex items-start text-gray-300">
                      <CheckCircle className="h-5 w-5 text-green-400 mr-3 mt-0.5 flex-shrink-0" />
                      {tip}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Examples Section */}
            <div className="bg-gradient-to-r from-purple-800/30 to-pink-800/30 rounded-2xl p-8 mb-12">
              <h3 className="text-2xl font-bold text-white text-center mb-6">
                Successful {genreData.title} Examples
              </h3>
              <div className="flex flex-wrap justify-center gap-4">
                {genreData.examples.map((example, index) => (
                  <div key={index} className="bg-white/10 rounded-lg px-4 py-2">
                    <span className="text-white font-medium">{example}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Marketing Tips */}
        <div className="py-16 px-4 bg-gray-900/50">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12">
              {genreData.title} Book Marketing Strategies
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {genreData.marketingTips.map((tip, index) => (
                <div key={index} className="bg-gray-800 rounded-xl p-6 flex items-start">
                  <div className="bg-green-600 rounded-full p-2 mr-4 flex-shrink-0">
                    <Target className="h-5 w-5 text-white" />
                  </div>
                  <p className="text-gray-300">{tip}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="py-16 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="bg-gradient-to-r from-purple-800 to-pink-800 rounded-2xl p-12">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Ready to Create Your {genreData.title} Book?
              </h2>
              <p className="text-xl text-gray-200 mb-8">
                Join thousands of authors who've transformed their ideas into published books
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <Link
                  to="/generate"
                  className="bg-white text-purple-900 font-bold py-4 px-8 rounded-xl hover:bg-gray-100 transition-all duration-300 transform hover:scale-105 flex items-center"
                >
                  <Zap className="h-5 w-5 mr-2" />
                  Start Your Free Trial
                </Link>
                
                <Link
                  to="/kdp-covers"
                  className="border-2 border-white text-white font-bold py-4 px-8 rounded-xl hover:bg-white hover:text-purple-900 transition-all duration-300 flex items-center"
                >
                  <Palette className="h-5 w-5 mr-2" />
                  Create Cover First
                </Link>
              </div>
              
              <p className="text-gray-300 text-sm mt-4">
                No credit card required • 14-day free trial • Cancel anytime
              </p>
            </div>
          </div>
        </div>

        {/* Removed duplicate Related Pages section - using RelatedPSEOPages component instead */}

        {/* Internal Links for SEO */}
        <RelatedPSEOPages currentGenre={genre} currentTool={tool} maxLinks={6} />

        <Footer />
      </div>
    </>
  );
};

export default PSEOPage;